import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import type { IndustryLinkPlan } from "@/lib/internal-links";
import { trackEvent } from "@/lib/analytics";

interface Props {
  plan: IndustryLinkPlan;
  /** Sector or service name, used in headings so the anchor context stays specific. */
  sector: string;
  /** Changes the wording so service pages don't read as sector pages. */
  variant?: "industry" | "service";
  /** Groups to leave out, e.g. when a dedicated tools module renders them. */
  hideGroups?: Array<keyof IndustryLinkPlan>;
}

/** Group key -> GA4-friendly content type. */
const CONTENT_TYPE: Record<keyof IndustryLinkPlan, string> = {
  services: "service",
  articles: "article",
  calculators: "calculator",
  templates: "template",
};

const slugFromPath = (path: string) => path.split("?")[0].split("#")[0].replace(/\/+$/, "").split("/").pop() || path;

const GROUPS: Array<{
  key: keyof IndustryLinkPlan;
  heading: (s: string, variant: "industry" | "service") => string;
}> = [
  {
    key: "services",
    heading: (s, v) =>
      v === "service" ? "HR services that work alongside this one" : `HR services used most by ${s}`,
  },
  { key: "articles", heading: () => "HR help articles worth reading next" },
  { key: "calculators", heading: () => "Calculators for the numbers behind these decisions" },
  { key: "templates", heading: () => "Templates you can use today" },
];

export function IndustryLinkPlanSection({ plan, sector, variant = "industry", hideGroups = [] }: Props) {
  const groups = GROUPS.filter((g) => !hideGroups.includes(g.key) && plan[g.key].length > 0);
  if (groups.length === 0) return null;

  return (
    <section id="where-to-go-next" className="border-t border-border px-6 py-20 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent">
          Where to go next
        </p>
        <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-brand sm:text-4xl">
          {variant === "service"
            ? `Related HR guidance, tools and services for ${sector}`
            : `Related HR support, guidance and tools for ${sector}`}
        </h2>
        <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">
          {variant === "service"
            ? "Each link below covers a decision employers make while using this service, with the guidance, calculator or template that answers it."
            : "Each link below covers a decision employers in this sector make regularly, with the guidance, service or calculator that answers it."}
        </p>


        <div className="mt-12 grid gap-10 lg:grid-cols-2">
          {groups.map((group) => (
            <div key={group.key}>
              <h3 className="font-serif text-xl leading-tight text-brand">
                {group.heading(sector, variant)}
              </h3>
              <ul className="mt-5 space-y-4">
                {plan[group.key].map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      onClick={() =>
                        trackEvent("internal_link_click", {
                          category: "navigation",
                          action: "where-to-go-next",
                          label: link.label,
                          properties: {
                            module: "where_to_go_next",
                            content_type: CONTENT_TYPE[group.key],
                            destination_slug: link.slug ?? slugFromPath(link.path),
                            destination_path: link.path,
                            link_text: link.label,
                            source_variant: variant,
                            source_name: sector,
                            meta: link.meta,
                          },
                        })
                      }
                      className="group flex items-start gap-3 rounded-md border border-border bg-card p-4 transition-colors hover:border-accent/60 hover:bg-secondary"
                    >
                      <ArrowUpRight className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden="true" />
                      <span>
                        <span className="block text-sm font-medium text-foreground group-hover:text-brand">
                          {link.label}
                        </span>
                        {link.context ? (
                          <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                            {link.context}
                          </span>
                        ) : null}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
